import { c as _c } from "react/compiler-runtime";
import { Box, Flex, Heading, Icon, Stack, Table, Text, VisuallyHidden, useSlotRecipe } from '@chakra-ui/react';
import type { ScoProduct } from '@src/graphql-types';
import { paywallComparisonHighlightRecipe } from '@src/theme/slot-recipes/paywall-comparison-highlight';
import { LuCheck, LuMinus } from 'react-icons/lu';
import { orderProductsForIntent, resolvePaywallIntent, visiblePaywallFeatures, type PaywallFeature, type PaywallIntentId } from './intent';
import { productLabel, productPaywallIntent, productPriceLabel } from './presentation';
export type ProductDetailComparisonTableProps = {
  products: ScoProduct[];
  comparisonRows?: PaywallComparisonRow[];
  appearance?: 'standard' | 'quiet';
  intent?: PaywallIntentId;
  tokens?: Record<string, string>;
  /** When false, a parent page supplies the section heading. */
  showIntro?: boolean;
  /** `given` keeps the caller’s order. `recommended` leads with the intent’s plan. */
  productOrder?: 'recommended' | 'given';
  /**
   * A leading non-product column (e.g. Free) read from `comparisonRows` values by `key`. Only used
   * with caller rows, since a catalog product's features say nothing about the free tier.
   */
  baselineColumn?: PaywallComparisonBaseline;
};
export type PaywallComparisonBaseline = {
  key: string;
  title: string;
  priceLabel?: string;
};
export type PaywallComparisonValue = boolean | string;
export type PaywallComparisonRow = {
  key: string;
  title: string;
  values: Record<string, PaywallComparisonValue>;
};
function orderedProducts(products: ScoProduct[], intentId: PaywallIntentId | undefined, productOrder: 'recommended' | 'given') {
  const intent = resolvePaywallIntent(intentId);
  return productOrder === 'given' ? products : orderProductsForIntent(products, intent);
}
function unionRows(products: ScoProduct[], intentId: PaywallIntentId | undefined, productOrder: 'recommended' | 'given'): PaywallFeature[] {
  const intent = resolvePaywallIntent(intentId);
  const rows: PaywallFeature[] = [];
  const seen = new Set<string>();
  for (const product of orderedProducts(products, intentId, productOrder)) {
    for (const feature of visiblePaywallFeatures(product, intent)) {
      if (seen.has(feature.key)) {
        continue;
      }
      seen.add(feature.key);
      rows.push({
        ...feature,
        title: feature.compareTitle ?? feature.title
      });
    }
  }
  return rows;
}
export const ProductDetailComparisonTable = t0 => {
  const $ = _c(70);
  const {
    appearance: t1,
    baselineColumn,
    comparisonRows,
    intent,
    productOrder: t2,
    products,
    showIntro: t3,
    tokens
  } = t0;
  const appearance = t1 === undefined ? "standard" : t1;
  const productOrder = t2 === undefined ? "recommended" : t2;
  const showIntro = t3 === undefined ? true : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      recipe: paywallComparisonHighlightRecipe
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const highlightRecipe = useSlotRecipe(t4);
  let t5;
  if ($[1] !== appearance || $[2] !== highlightRecipe) {
    t5 = highlightRecipe({
      appearance
    });
    $[1] = appearance;
    $[2] = highlightRecipe;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const highlightStyles = t5;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== appearance || $[5] !== baselineColumn || $[6] !== comparisonRows || $[7] !== highlightStyles || $[8] !== intent || $[9] !== productOrder || $[10] !== products || $[11] !== showIntro || $[12] !== tokens) {
    t22 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolved = resolvePaywallIntent(intent, tokens);
      const columns = orderedProducts(products, intent, productOrder);
      const copy = columns[0] ? productPaywallIntent(columns[0], intent, tokens) : resolved;
      const rows = comparisonRows?.length ? comparisonRows : unionRows(products, intent, productOrder);
      const baseline = comparisonRows?.length ? baselineColumn : undefined;
      let t23;
      if ($[33] !== baseline) {
        t23 = row => baseline && "values" in row ? row.values[baseline.key] ?? false : false;
        $[33] = baseline;
        $[34] = t23;
      } else {
        t23 = $[34];
      }
      const baselineValue = t23;
      const featuresByProduct = new Map(columns.map(product => [product.id, new Map(visiblePaywallFeatures(product, resolved).map(_temp))]));
      const featureValue = (product_0, row_0) => {
        const feature_0 = featuresByProduct.get(product_0.id)?.get(row_0.key);
        return feature_0 ? feature_0.value ?? true : false;
      };
      const valueFor = (product_1, row_1) => "values" in row_1 ? row_1.values[product_1.productId || ""] ?? false : featureValue(product_1, row_1);
      const differs = row_2 => new Set([...(baseline ? [String(baselineValue(row_2))] : []), ...columns.map(product_2 => String(valueFor(product_2, row_2)))]).size > 1;
      const columnCount = columns.length + (baseline ? 1 : 0);
      const differingRows = columnCount > 1 ? rows.filter(differs) : [];
      const groups = differingRows.length > 0 && differingRows.length < rows.length ? [{
        label: "What changes",
        rows: differingRows
      }, {
        label: comparisonRows?.length ? "Same across plans" : "Included in every plan",
        rows: rows.filter(row_3 => !differs(row_3))
      }] : [{
        label: "What\u2019s included",
        rows
      }];
      const tableMinW = `${8 + columnCount * 4.5}rem`;
      const rowCell = _temp2;
      if (columns.length === 0) {
        let t24;
        if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
          t24 = <Text color="fg.muted" textStyle="sm">Plan comparison isn’t available right now.</Text>;
          $[35] = t24;
        } else {
          t24 = $[35];
        }
        t22 = t24;
        break bb0;
      }
      T2 = Stack;
      t19 = 4;
      t20 = showIntro ? <Stack gap={2}><Heading size="xl">{copy.headline}</Heading><Text color="fg.muted" textStyle="sm">{copy.supporting}</Text></Stack> : null;
      if ($[36] !== columnCount) {
        t21 = columnCount > 3 ? <Text color="fg.muted" display={{
          base: "block",
          md: "none"
        }} textStyle="xs">Scroll sideways to compare plans</Text> : null;
        $[36] = columnCount;
        $[37] = t21;
      } else {
        t21 = $[37];
      }
      T1 = Box;
      t14 = "Plan comparison";
      t15 = "auto";
      t16 = "relative";
      t17 = "region";
      t18 = 0;
      T0 = Table.Root;
      t6 = "What each plan includes";
      t7 = "separate";
      t8 = 0;
      t9 = tableMinW;
      t10 = "sm";
      t11 = "fixed";
      const t24 = columnCount > 2 ? "36%" : "40%";
      const t25 = appearance === "quiet" ? "42%" : "38%";
      let t26;
      if ($[38] !== t24 || $[39] !== t25) {
        t26 = {
          base: t24,
          md: t25
        };
        $[38] = t24;
        $[39] = t25;
        $[40] = t26;
      } else {
        t26 = $[40];
      }
      let t27;
      if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = <VisuallyHidden>Feature</VisuallyHidden>;
        $[41] = t27;
      } else {
        t27 = $[41];
      }
      let t28;
      if ($[42] !== t26) {
        t28 = <Table.ColumnHeader bg="transparent" borderBottomWidth={0} scope="col" w={t26}>{t27}</Table.ColumnHeader>;
        $[42] = t26;
        $[43] = t28;
      } else {
        t28 = $[43];
      }
      let t29;
      if ($[44] !== baseline) {
        t29 = baseline ? <Table.ColumnHeader bg="transparent" borderBottomWidth={0} color="fg" px={1} py={3} scope="col" textAlign="center" textStyle="sm"><Text fontWeight="semibold" textWrap="balance">{baseline.title}</Text>{baseline.priceLabel ? <Text color="fg.muted" fontWeight="normal" textStyle="xs">{baseline.priceLabel}</Text> : null}</Table.ColumnHeader> : null;
        $[44] = baseline;
        $[45] = t29;
      } else {
        t29 = $[45];
      }
      t12 = <Table.Header><Table.Row bg="transparent">{t28}{t29}{columns.map(product_3 => {
            const highlighted = product_3.productId === resolved.recommendedProductId;
            return <Table.ColumnHeader key={product_3.productId || product_3.id} bg={highlighted ? undefined : "transparent"} borderBottomWidth={0} borderTopRadius="md" color={highlighted ? undefined : "fg"} css={highlighted ? highlightStyles.header : undefined} px={1} py={3} scope="col" textAlign="center" textStyle="sm"><Text fontWeight="semibold" textWrap="balance">{productLabel(product_3)}</Text><Text color={highlighted ? undefined : "fg.muted"} css={highlighted ? highlightStyles.price : undefined} fontWeight="normal" textStyle="xs">{productPriceLabel(product_3)}</Text></Table.ColumnHeader>;
          })}</Table.Row></Table.Header>;
      t13 = groups.filter(_temp3).map(group_0 => <Table.Body key={group_0.label}><Table.Row bg="transparent"><Table.Cell as="th" bg="transparent" borderBottomWidth={0} color="fg.muted" colSpan={columnCount + 1} pb={2} ps={0} pt={6} scope="colgroup" textStyle="label2xs">{group_0.label}</Table.Cell></Table.Row>{group_0.rows.map((row_4, rowIndex) => {
          const last_0 = rowIndex === group_0.rows.length - 1;
          return <Table.Row key={row_4.key} bg="transparent"><Table.Cell {...rowCell(last_0)} as="th" bg="transparent" borderTopColor="border" borderTopWidth={rowIndex === 0 ? "1px" : 0} fontWeight="normal" pe={3} ps={0} scope="row" textAlign="start" textStyle="sm">{row_4.title}</Table.Cell>{baseline ? <Table.Cell {...rowCell(last_0)} bg="transparent" borderTopColor="border" borderTopWidth={rowIndex === 0 ? "1px" : 0} px={1} textAlign="center"><ComparisonValue value={baselineValue(row_4)} /></Table.Cell> : null}{columns.map(product_4 => {
              const value = valueFor(product_4, row_4);
              const highlighted_0 = product_4.productId === resolved.recommendedProductId;
              return <Table.Cell key={product_4.productId || product_4.id} {...rowCell(last_0)} bg={highlighted_0 ? undefined : "transparent"} borderTopColor="border" borderTopWidth={rowIndex === 0 ? "1px" : 0} css={highlighted_0 ? highlightStyles.cell : undefined} px={1} textAlign="center"><ComparisonValue value={value} /></Table.Cell>;
            })}</Table.Row>;
        })}</Table.Body>);
    }
    $[4] = appearance;
    $[5] = baselineColumn;
    $[6] = comparisonRows;
    $[7] = highlightStyles;
    $[8] = intent;
    $[9] = productOrder;
    $[10] = products;
    $[11] = showIntro;
    $[12] = tokens;
    $[13] = T0;
    $[14] = T1;
    $[15] = T2;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
    $[24] = t18;
    $[25] = t19;
    $[26] = t20;
    $[27] = t21;
    $[28] = t22;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    T0 = $[13];
    T1 = $[14];
    T2 = $[15];
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t14 = $[20];
    t15 = $[21];
    t16 = $[22];
    t17 = $[23];
    t18 = $[24];
    t19 = $[25];
    t20 = $[26];
    t21 = $[27];
    t22 = $[28];
    t6 = $[29];
    t7 = $[30];
    t8 = $[31];
    t9 = $[32];
  }
  if (t22 !== Symbol.for("react.early_return_sentinel")) {
    return t22;
  }
  let t23;
  if ($[46] !== T0 || $[47] !== t10 || $[48] !== t11 || $[49] !== t12 || $[50] !== t13 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8 || $[54] !== t9) {
    t23 = <T0 aria-label={t6} borderCollapse={t7} borderSpacing={t8} minW={t9} size={t10} tableLayout={t11}>{t12}{t13}</T0>;
    $[46] = T0;
    $[47] = t10;
    $[48] = t11;
    $[49] = t12;
    $[50] = t13;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== T1 || $[57] !== t14 || $[58] !== t15 || $[59] !== t16 || $[60] !== t17 || $[61] !== t18 || $[62] !== t23) {
    t24 = <T1 aria-label={t14} overflowX={t15} position={t16} role={t17} tabIndex={t18}>{t23}</T1>;
    $[56] = T1;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== T2 || $[65] !== t19 || $[66] !== t20 || $[67] !== t21 || $[68] !== t24) {
    t25 = <T2 gap={t19}>{t20}{t21}{t24}</T2>;
    $[64] = T2;
    $[65] = t19;
    $[66] = t20;
    $[67] = t21;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  return t25;
};
const ComparisonValue = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = typeof value === "boolean" ? <><Flex justify="center"><Icon boxSize={value ? 4 : 3.5} color={value ? "orange.fg" : "fg.subtle"} strokeWidth={value ? 2.5 : 2}>{value ? <LuCheck aria-hidden={true} /> : <LuMinus aria-hidden={true} />}</Icon></Flex><VisuallyHidden>{value ? "Included" : "Not included"}</VisuallyHidden></> : <Text fontVariantNumeric="tabular-nums" fontWeight="semibold" textStyle="xs">{value}</Text>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(feature) {
  return [feature.key, feature];
}
function _temp2(last) {
  return {
    borderBottomWidth: last ? 0 : "1px",
    borderColor: "border.muted",
    py: {
      base: 2.5,
      md: 3
    }
  } as const;
}
function _temp3(group) {
  return group.rows.length > 0;
}